import { AsyncPipe } from '@angular/common';
import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    DestroyRef,
    ElementRef,
    HostBinding,
    HostListener,
    inject,
    Input,
    QueryList,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Subject, debounceTime } from 'rxjs';
import { TabType } from '../../_shared/types/tab.type';
import { getRovingIndex } from '../../_shared/utils/roving-index';
import { nextTabGroupId } from '../tab-group-id';
import { TabComponent } from '../tab.component';
import { TabGroupBaseDirective } from './tab-group-base.directive';
import { GtmService } from '../../_shared/services/gtm.service';
import { SnowplowService } from '../../_shared/services/snowplow.service';
import { TrackingEvents } from '../../_shared/data/tracking-events.data';
import { ICustomWindow, WindowRefService } from '../../_shared/services/window-ref.service';
import { SpacerComponent } from '../../spacer/spacer.component';
import { ContainerComponent } from '../../container/container.component';

@Component({
    selector: 'sirius-tab-group',
    templateUrl: './tab-group.component.html',
    styleUrls: ['./tab-group.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpacerComponent, AsyncPipe, ContainerComponent],
})
export class TabGroupComponent extends TabGroupBaseDirective implements AfterViewInit, AfterContentInit {
    @Input() size: TabType = 'large';
    @Input() topSpacer = true;

    tabComponents: TabComponent[] = [];

    readonly groupId = nextTabGroupId();

    @ContentChildren(TabComponent)
    private _tabComponents!: QueryList<TabComponent>;

    @ViewChild('tabs', { read: ElementRef<HTMLElement> })
    private _tabs!: ElementRef<HTMLElement>;

    @ViewChild('scrollTarget', { read: ElementRef<HTMLElement> })
    private _scrollTarget!: ElementRef<HTMLElement>;

    private _gtmService = inject(GtmService);
    private _snowplowService = inject(SnowplowService);
    private _windowRef = inject(WindowRefService);
    private _destroyRef = inject(DestroyRef);
    private _resize = new Subject<void>();

    private _isStuck = new BehaviorSubject<boolean>(false);
    isStuck$ = this._isStuck.asObservable();

    private get isStuck(): boolean {
        return this._isStuck.getValue();
    }

    private set isStuck(value: boolean) {
        this._isStuck.next(value);
    }

    private get Window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }

    @HostBinding('class.sir-small')
    get isSmall(): boolean {
        return this.size === 'small';
    }

    @HostListener('window:scroll', [])
    onScroll(): void {
        this.setIsStuck();
    }

    @HostListener('window:resize')
    onResize(): void {
        this._resize.next();
    }

    ngAfterContentInit(): void {
        this.syncTabs();
        this._tabComponents.changes.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => this.syncTabs());
    }

    ngAfterViewInit(): void {
        this._resize.pipe(debounceTime(200), takeUntilDestroyed(this._destroyRef)).subscribe(() => this.setIsStuck());
        this.setIsStuck();
    }

    onClick(event: MouseEvent, index: number): void {
        const element = event.target as HTMLElement;
        this.setActiveTab(element, index);

        if (!this.isStuck) {
            return;
        }

        this._scrollTarget.nativeElement.scrollIntoView();
    }

    onKeydown(event: KeyboardEvent): void {
        const nextIndex = getRovingIndex(event.key, this.currentIndex, this.tabComponents.length, 'horizontal');
        if (nextIndex === null) return;

        event.preventDefault();

        const tablist = (event.currentTarget as HTMLElement).closest('[role="tablist"]');
        const nextTab = tablist?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIndex];

        this.setActiveTab(nextTab ?? (event.currentTarget as HTMLElement), nextIndex);
        nextTab?.focus();
    }

    private setActiveTab(element: HTMLElement, index: number) {
        this.tabComponents[this.currentIndex].active = false;
        this.tabComponents[index].active = true;
        this.currentIndex = index;
        this.addTracking(element);
    }

    /** Without this, tab panels render with no `role`/`id` and `aria-controls` points at nothing. */
    private syncTabs(): void {
        this.tabComponents = this._tabComponents.toArray();
        this.currentIndex = Math.min(this.currentIndex, Math.max(0, this.tabComponents.length - 1));

        this.tabComponents.forEach((tab, index) => {
            tab.panelId = `${this.groupId}-panel-${index}`;
            tab.labelledById = `${this.groupId}-tab-${index}`;
        });
    }

    private setIsStuck(): void {
        if (!this.Window) {
            return;
        }

        const posY = this._tabs.nativeElement.getBoundingClientRect().top;
        if (!this.isStuck) {
            if (posY < 1.0) {
                this.isStuck = true;
            }
        } else if (posY > 1.0) {
            this.isStuck = false;
        }
    }

    private addTracking(element: HTMLElement) {
        const cfg = {
            ...this.config?.tracking,
            index: this.currentIndex,
        };

        this._gtmService.track(TrackingEvents.gtm['tabSelect']([cfg]));

        const cfg2 = {
            element_classes: Array.from(element.classList),
            element_content: this.tabComponents[this.currentIndex].title,
            identifier: null, // TODO: Get identifier for tab select
            index: this.currentIndex,
            context: [],
        };

        this._snowplowService.track('tabSelect', cfg2);
    }
}
